"use client";

import { Activity } from "lucide-react";
import type { CSSProperties } from "react";

import { useCcLatchInView } from "@/lib/use-cc-latch-in-view";
import type { SeoWorkflowTrackCardCopy } from "./seo-workflow-copy";
import { CcSeoWorkflowCard } from "./CcSeoWorkflowCard";

function formatTrendChange(trend: "up" | "down", change: number) {
  return trend === "up" ? `+${change}` : `-${change}`;
}

function TrendIcon({ trend }: { trend: "up" | "down" }) {
  if (trend === "up") {
    return (
      <svg
        className="cc-seo-workflow-track__trend-icon"
        width="10"
        height="10"
        viewBox="0 0 10 10"
        aria-hidden="true"
      >
        <path d="M5 1.5L8.5 6H6.25V8.5H3.75V6H1.5L5 1.5Z" fill="currentColor" />
      </svg>
    );
  }

  return (
    <svg
      className="cc-seo-workflow-track__trend-icon"
      width="10"
      height="10"
      viewBox="0 0 10 10"
      aria-hidden="true"
    >
      <path d="M5 8.5L1.5 4H3.75V1.5H6.25V4H8.5L5 8.5Z" fill="currentColor" />
    </svg>
  );
}

function TrackDashboardMockup({ copy }: { copy: SeoWorkflowTrackCardCopy }) {
  const { ref: panelRef, isInView } = useCcLatchInView<HTMLDivElement>({
    threshold: 0.35,
  });

  return (
    <div className="cc-seo-workflow-track">
      <div
        ref={panelRef}
        className={[
          "cc-seo-workflow-track__panel",
          isInView && "cc-seo-workflow-track__panel--in-view",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="cc-seo-workflow-track__header">
          <div className="cc-seo-workflow-track__title-group">
            <svg
              className="cc-seo-workflow-track__chart-icon"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 19V11M10 19V5M16 19V9M22 19V3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            <span className="cc-seo-workflow-track__label">{copy.panelTitle}</span>
          </div>
          <span className="cc-seo-workflow-track__location">{copy.location}</span>
        </div>

        <ul className="cc-seo-workflow-track__list" tabIndex={0}>
          {copy.keywords.map((row, index) => (
            <li
              key={row.keyword}
              className="cc-seo-workflow-track__row"
              style={{ "--track-row-index": index } as CSSProperties}
            >
              <span
                className={`cc-seo-workflow-track__rank${
                  row.rank <= 3 ? " cc-seo-workflow-track__rank--top" : ""
                }`}
              >
                #{row.rank}
              </span>
              <span className="cc-seo-workflow-track__keyword">{row.keyword}</span>
              <span
                className={`cc-seo-workflow-track__trend cc-seo-workflow-track__trend--${row.trend}`}
              >
                <TrendIcon trend={row.trend} />
                {formatTrendChange(row.trend, row.change)}
              </span>
              <svg
                className="cc-seo-workflow-track__sparkline"
                viewBox="0 0 40 16"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  className="cc-seo-workflow-track__sparkline-path"
                  pathLength={1}
                  d={row.sparkline}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

type CcSeoWorkflowTrackProps = {
  copy: SeoWorkflowTrackCardCopy;
};

export function CcSeoWorkflowTrack({ copy }: CcSeoWorkflowTrackProps) {
  return (
    <CcSeoWorkflowCard
      id="track"
      title={copy.title}
      description={copy.description}
      badge={copy.badge}
      icon={Activity}
    >
      <TrackDashboardMockup copy={copy} />
    </CcSeoWorkflowCard>
  );
}
